<template>

    <Combobox :modelValue="modelValue" @update:modelValue="value => emit('onRegionSelected', value)"
        :multiple="allowMultiple" as="div" class="">
        <div class="relative mt-4">
            <ComboboxInput
                class="w-full py-2 pl-3 pr-10 bg-white border border-clay-500 rounded-md shadow-sm focus:border-clay-500 focus:outline-hidden focus:ring-1 focus:ring-clay-500 sm:text-sm"
                placeholder="Start typing a region.." :displayValue="allowMultiple ? '' : (t) => t.name"
                @change="territoryQuery = $event.target.value" />
            <ComboboxButton aria-label="Expand Regions Button" class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-hidden">
            </ComboboxButton>

            <ComboboxOptions
                class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm">
                <ComboboxOption v-for="territory in filteredTerritories" :key="territory.id"
                    v-slot="{ active, selected }" :value="territory"
                    as="template">
                    <li
                        :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-clay-600 text-white' : 'text-gray-900']">
                        <span :class="['block truncate', selected && 'font-semibold']">
                            {{ territory.name }}
                        </span>
                        <span v-if="selected"
                            :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-clay-600']">
                            <CheckIcon aria-hidden="true" class="w-5 h-5" />
                        </span>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
        <p v-if="filteredTerritories.length > 0"  class="px-2 py-2 text-xs text-clay-800">Type in a city or region name and select from the dropdown list.</p>
        <p v-else class="px-2 py-2 text-xs text-red-500">The city you entered is not in our database. Please input a larger nearby city, or select a city from the list below. 
        </p>
    </Combobox>
    <div class="relative mt-4">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-clay-500"></div>
        </div>
        <div class="relative flex justify-center text-sm"><span class="px-2 text-clay-800 bg-white"> OR </span></div>
    </div>
    <div class="relative w-full max-w-full text-left">
        <div class="mt-3">
            <button id="menu-button"
                class="inline-flex items-center min-w-full px-3 py-2 mx-auto text-sm font-medium border border-clay-500 rounded-md shadow-sm text-clay-950 hover:bg-clay-50 bg-clay-100"
                type="button" @click.prevent="openBrowseMore =! openBrowseMore">
                Browse By Region
                <ChevronUpIcon v-if="openBrowseMore" class="w-5 h-5 ml-2 -mr-1" />
                <ChevronDownIcon v-else class="w-5 h-5 ml-2 -mr-1" />
            </button>
        </div>
        <div v-if="openBrowseMore" aria-labelledby="menu-button" aria-orientation="vertical"
            class="px-6 pb-6 rounded-lg bg-zinc-50" role="menu" tabindex="-1"
            x-transition:enter="transition ease-out duration-200"
            x-transition:enter-end="transform opacity-100 scale-100"
            x-transition:enter-start="transform opacity-0 scale-95" x-transition:leave="transition ease-in duration-75"
            x-transition:leave-end="transform opacity-0 scale-95"
            x-transition:leave-start="transform opacity-100 scale-100">


            <div class="mt-4">
                <div class="sm:hidden">
                    <label for="tabs" class="sr-only">Select a region</label>
                    <select id="tabs" name="tabs"
                        class="block w-full px-3 py-2 border rounded-md border-clay-300 focus:border-clay-500 focus:ring-clay-500 dark:text-white"
                        v-model="currentTab">
                        <option v-for="tab in tabs" :key="tab.name" :value="tab"
                            :selected="tab.name === currentTab.name">
                            {{ tab.name }}
                        </option>
                    </select>
                </div>
                <div class="hidden sm:block">
                    <div class="border-b border-gray-200">
                        <nav class="flex -mb-px" aria-label="Tabs">
                            <a v-for="tab in tabs" :key="tab.name" @click.prevent="onTabChanged(tab)"
                                :class="[tab.name === currentTab.name ? 'border-clay-500 text-clay-600' : 'border-transparent text-clay-800 hover:border-clay-500 hover:text-gray-700', 'w-1/4 border-b-2 py-4 px-1 text-center text-sm font-medium']">{{
                                tab.name }} ({{getTerritories(tab.name).length}})</a>
                        </nav>
                    </div>
                </div>
            </div>
            <div class="px-2 mt-4 space-y-1" style="max-height: 400px; overflow-y: scroll">
                <div v-if="currentTab.name ==='United States'">
                    <div class="mt-4" v-for="[states, territories] in usTerritoriesGroupedByState">
                        <h4 class="text-sm font-medium text-gray-700 underline">{{states}}</h4>
                        <div class="w-full py-1 md:grid md:grid-cols-3" role="none">
                            <a v-for="territory in territories" :key="territory.id" :alt="territory.name"
                                :title="territory.name" @click.prevent="onRegionSelected(territory)"
                                class="block px-2 py-2 text-sm text-gray-700 hover:bg-zinc-100 hover:font-bold hover:text-clay-950">{{
                                territory.name }}</a>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="w-full py-1 md:grid md:grid-cols-2" role="none">
                        <a v-for="territory in getTerritories(currentTab.name)" :alt="territory.name"
                            @click.prevent="onRegionSelected(territory)"
                            class="block px-2 py-2 text-sm text-gray-700 hover:bg-zinc-100 hover:font-bold hover:text-clay-950"
                            title="">{{ territory.name }}</a>
                    </div>
                </div>
            </div>

        </div>
    </div>

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, defineEmits, defineProps, Ref, ref} from "vue";
import {
    Region,
} from "@/dtos";

import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions} from '@headlessui/vue';
import {CheckIcon, ChevronDownIcon,ChevronUpIcon} from '@heroicons/vue/20/solid';

import _ from "lodash";

const props = defineProps({
    territories: {
        type: Array,
        default: []
    },
    allowMultiple:{
        type: Boolean,
        default: false
    },
    modelValue: {
        type: Object,
        default: {}
    }
})

const openBrowseMore = ref(false);

const emit = defineEmits(['update:modelValue', 'onRegionSelected'])

const territoryQuery = ref('');

const filteredTerritories = computed(() =>
    territoryQuery.value === ''
        ? props.territories
        : props.territories.filter((territory: Region) => {
                return territory.name.toLowerCase().includes(territoryQuery.value.toLowerCase())
            }
        )
)

const usTerritoriesGroupedByState = computed(() => {
    let groupedByStateMap = new Map(Object.entries(_.groupBy(getTerritories("United States"), x=>x.states.join(", "))));
    let groupedByStateSorted = new Map([...groupedByStateMap.entries()].sort());
    //console.log(groupedByStateSorted);
    return groupedByStateSorted
});

const getTerritories = (name: string) => {
    switch (name) {
        case "UK":
            return props.territories.filter((t: Region) => t.country === "United Kingdom");

        case "More":
            return props.territories.filter((t: Region) => t.country 
                !== "United States"
                && t.country !== "Canada"
                && t.country !== "Australia"
                && t.country !== "United Kingdom");
            
        default:
            return props.territories.filter((t: Region) => t.country === name)
    }
}

const tabs = ref([
    {
        name: "United States"
    },
    {
        name: "Canada",
    },
    {
        name: "UK",
    },
    {
        name: "Australia",
    },
    {
        name: "More"
    }
]);

let currentTab = ref(tabs.value[0]);

const onTabChanged = (tab: any) => {
    currentTab.value = tab;
};

const onRegionSelected = (territory: Region) =>{
    emit('onRegionSelected', territory);
    openBrowseMore.value = false;
}

</script>
<style>

</style>